// BUILD-042 cycle 12 - Benefits step / eligibility editor: the pure rules (no DOM, no React, no network). Tested under plain Node (benefitsModel.test.mjs).
//
// What this module decides, and what it does NOT:
//   * A plan "needs a decision" until HR chooses Eligible (with a date) or Not eligible. `not_reviewed` stays the undecided state - it is never inferred.
//   * A waiting-period default only SUGGESTS a date: hire date + N CALENDAR days (never months). No rule, or no hire date => no suggestion, nothing invented.
//   * HR confirms or overrides the date. A saved decision is never rewritten because a plan default or a hire date changed (nothing here recomputes a saved date).
//   * The server enforces "Eligible needs a date" again, in the write lock; this is the same rule shown before the request is sent.
import type { HrBenefitEligibilityStatus } from '../../../api/client';

export interface BenefitLike {
  planId: string;
  name: string;
  published: boolean;
  status: HrBenefitEligibilityStatus;
  eligibilityDate: string | null;
  note?: string | null;
  waitingPeriodDays?: number | null;
}

// ─── Dates (calendar arithmetic in UTC so a time zone or DST can never shift a day) ──────────────────────────────────────────────
const ISO = /^(\d{4})-(\d{2})-(\d{2})$/;
export function isRealIsoDate(s: string | null | undefined): s is string {
  const m = ISO.exec(s ?? '');
  if (!m) return false;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return d.getUTCFullYear() === Number(m[1]) && d.getUTCMonth() === Number(m[2]) - 1 && d.getUTCDate() === Number(m[3]);
}
/** hire date + N CALENDAR days, as YYYY-MM-DD; null when either input is not usable. */
export function addCalendarDays(iso: string | null | undefined, days: number): string | null {
  if (!isRealIsoDate(iso) || !Number.isInteger(days)) return null;
  const m = ISO.exec(iso)!;
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + days)).toISOString().slice(0, 10);
}
/** YYYY-MM-DD -> MM/DD/YYYY (the platform's fixed display order). Anything else is returned unchanged. */
export function usDate(iso: string | null | undefined): string {
  const m = ISO.exec(iso ?? '');
  return m ? `${m[2]}/${m[3]}/${m[1]}` : (iso ?? '');
}

export interface Suggestion { date: string; label: string }
/** The suggested eligible date for a plan, or null when the plan has no rule, the rule is not a whole number 0..3650, or the hire date is unknown. */
export function suggestEligibleDate(hireDate: string | null | undefined, waitingPeriodDays: number | null | undefined): Suggestion | null {
  if (typeof waitingPeriodDays !== 'number' || !Number.isInteger(waitingPeriodDays) || waitingPeriodDays < 0 || waitingPeriodDays > 3650) return null;
  const date = addCalendarDays(hireDate, waitingPeriodDays);
  if (!date) return null;
  return { date, label: `Suggested: ${usDate(date)} (hire date + ${waitingPeriodDays} ${waitingPeriodDays === 1 ? 'day' : 'days'})` };
}

// ─── What counts as decided / applicable ──────────────────────────────────────────────────────────────────────────────────────────
/** A decision HR has actually made: Not eligible, or Eligible with a real date. A legacy Eligible row with no date is NOT a valid decision. */
export function isDecided(b: Pick<BenefitLike, 'status' | 'eligibilityDate'>): boolean {
  return b.status === 'not_eligible' || (b.status === 'eligible' && isRealIsoDate(b.eligibilityDate));
}
/** The plans HR must decide in setup: published ones (the list a person sees never includes retired plans). */
export const isApplicable = (b: Pick<BenefitLike, 'published'>): boolean => b.published;

export interface ReviewCounts { applicable: number; decided: number; undecided: BenefitLike[] }
export function reviewCounts(entries: BenefitLike[]): ReviewCounts {
  const app = entries.filter(isApplicable);
  const undecided = app.filter((b) => !isDecided(b));
  return { applicable: app.length, decided: app.length - undecided.length, undecided };
}

export interface AckSpec { label: string; disabled: boolean; hint: string | null }
/** The footer acknowledgment. Enabled only when every applicable plan is decided, or when there are none (then the label says so). */
export function benefitsAck(counts: ReviewCounts): AckSpec {
  if (counts.applicable === 0) return { label: 'No benefits to review', disabled: false, hint: null };
  if (counts.undecided.length > 0) {
    const n = counts.undecided.length;
    return { label: 'Benefits reviewed', disabled: true, hint: `${n} ${n === 1 ? 'benefit needs' : 'benefits need'} a decision first.` };
  }
  return { label: 'Benefits reviewed', disabled: false, hint: null };
}

/** Bulk "Not eligible for all": ONLY applicable plans with no decision at all. An existing decision (even an incomplete legacy Eligible) is never a target. */
export function bulkNotEligibleTargets<T extends BenefitLike>(entries: T[]): T[] {
  return entries.filter((b) => isApplicable(b) && b.status === 'not_reviewed');
}

// ─── The per-plan draft ───────────────────────────────────────────────────────────────────────────────────────────────────────────
export type DraftStatus = '' | 'eligible' | 'not_eligible';
export interface EligibilityDraft { status: DraftStatus; date: string; note: string }

export function draftFromEntry(b: BenefitLike): EligibilityDraft {
  if (b.status === 'eligible') return { status: 'eligible', date: b.eligibilityDate ?? '', note: b.note ?? '' };
  if (b.status === 'not_eligible') return { status: 'not_eligible', date: '', note: '' };
  return { status: '', date: '', note: '' };
}
export const DATE_REQUIRED_MESSAGE = 'Choose the date this person becomes eligible. The employee is shown “Eligible starting” that date.';
export const CHOICE_REQUIRED_MESSAGE = 'Choose Eligible or Not eligible.';
/** Why the draft cannot be saved yet, or null. Eligible REQUIRES a real date; Not eligible needs nothing else. */
export function draftProblem(d: EligibilityDraft): string | null {
  if (d.status === '') return CHOICE_REQUIRED_MESSAGE;
  if (d.status === 'eligible' && !isRealIsoDate(d.date)) return DATE_REQUIRED_MESSAGE;
  return null;
}
/** Has the person changed anything compared with what is saved? (Switching Not eligible -> Eligible without a date is still a change.) */
export function draftChanged(b: BenefitLike, d: EligibilityDraft): boolean {
  if (d.status === '') return false;
  const saved = draftFromEntry(b);
  if (b.status === 'not_reviewed') return true;
  if (saved.status !== d.status) return true;
  if (d.status === 'eligible' && saved.date !== d.date) return true;
  return saved.note.trim() !== d.note.trim();
}
/** The body for the per-plan eligibility writer. Date and (optional) note belong to Eligible only; a Not eligible decision stores neither input. */
export function eligibilityPayload(d: EligibilityDraft): { status: HrBenefitEligibilityStatus; eligibilityDate: string | null; note: string | null } {
  if (d.status === 'eligible') return { status: 'eligible', eligibilityDate: isRealIsoDate(d.date) ? d.date : null, note: d.note.trim() || null };
  return { status: 'not_eligible', eligibilityDate: null, note: null };
}
